import {
  Datagrid,
  Labeled,
  NumberField,
  RaRecord,
  ReferenceArrayField,
  SelectField,
  TextField,
  useRecordContext,
} from "react-admin";
import { MaintenanceResourcesEnum, SiteResourcesEnum } from "shared/enums/resourceMappings";

import { Box, Stack } from "@mui/material";

import { Avatar, Card, CardActions, CardContent, CardHeader, Divider } from "@mui/material";
import { MaintenancesList } from "entities/maintenancesService/MaintenancesList";
import SiteTelemetry from "features/charts/grafana/SiteTelemetry";
import PlanningChart from "features/charts/planning/PlanningChart";
import "react-big-scheduler-stch/lib/css/style.css";
import CustomDateField from "shared/components/fields/CustomDateField";
import { siteModeChoices } from "shared/enums/reactAdminChoices";
import { StatusesSimpleList } from "../../sites_statuses/StatusesSimpleList";
import { SiteContryEnum } from "shared/enums/globalEnums";

type SectionCardContainerProps = {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
};

const SectionCardContainer = ({ children, title, subtitle, actions }: SectionCardContainerProps) => {
  const titleFirstLetter = title ? title?.charAt(0).toUpperCase() : null;
  return (
    <Card elevation={4}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: "h6" }}
        subheader={subtitle}
        avatar={
          titleFirstLetter ? (
            <Avatar aria-label="title" sx={{ bgcolor: "primary.main" }}>
              {titleFirstLetter}
            </Avatar>
          ) : (
            <></>
          )
        }
      />
      <Divider />

      <CardContent>{children}</CardContent>
      <CardActions>{actions}</CardActions>
    </Card>
  );
};

type LabeledFieldProps = {
  source: string;
  label?: string;
  record?: RaRecord;
  ComponentField?: React.ComponentType<any>;
};

const LabeledField = ({ source, label, ComponentField = TextField }: LabeledFieldProps) => (
  <Labeled source={source} label={label || source}>
    <ComponentField source={source} fullWidth />
  </Labeled>
);

const TrancheTmSiteField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  if (!record || record.country !== SiteContryEnum.France) return <></>;
  return <LabeledField source={source} record={record} label={"Tranche TM site"} />;
};

const GeneralInfos = () => {
  const record = useRecordContext();
  if (!record || !record.id) return <></>;
  const { id } = record;
  return (
    <Stack direction={"column"} spacing={2}>
      <SectionCardContainer title="Site Infos">
        <Box display={"grid"} gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }} gap={2}>
          <LabeledField source="name" />
          <LabeledField source="dso" />
          <TrancheTmSiteField source="rte_code" />
          <LabeledField source="asset_id" />
          <LabeledField source="city" />
          <LabeledField source="zipcode" />
          <LabeledField source="country" />
          <Labeled source="mode">
            <SelectField source={"mode"} choices={siteModeChoices} />
          </Labeled>
          <LabeledField source="soc_reserve_inf" ComponentField={NumberField} />
          <LabeledField source="soc_inf" ComponentField={NumberField} />
          <LabeledField source="soc_sup" ComponentField={NumberField} />
          <LabeledField source="soc_reserve_up" ComponentField={NumberField} />
          <LabeledField source="useful_energy_at_1mw" ComponentField={NumberField} />
        </Box>
      </SectionCardContainer>
      <SectionCardContainer title="Group Infos">
        <ReferenceArrayField source="group_ids" reference={SiteResourcesEnum.Groups}>
          <Datagrid bulkActionButtons={false}>
            <TextField source="name" />
            <TextField source="code" />
            <CustomDateField source="created_at" />
          </Datagrid>
        </ReferenceArrayField>
      </SectionCardContainer>
      <SectionCardContainer title="Telemetry Chart" subtitle="Telemetry over the last hour">
        <SiteTelemetry />
      </SectionCardContainer>
      <SectionCardContainer title="Planning Charts" subtitle={"Plannings and masks in range"}>
        <PlanningChart siteId={id} />
      </SectionCardContainer>
      <SectionCardContainer title="Maintenances" subtitle="Current Site maintenances">
        <MaintenancesList
          resource={MaintenanceResourcesEnum.Maintenance}
          filter={{ site_id: id, end__gt: new Date().toISOString() }}
        />
      </SectionCardContainer>
      <SectionCardContainer title="Statuses" subtitle="All site statuses">
        <StatusesSimpleList filterDefaultValues={{ site_id: id }} resource={SiteResourcesEnum.StatusesV2} mode="site" />
      </SectionCardContainer>
    </Stack>
  );
};

export default GeneralInfos;
